import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import api from './api/axiosInstance';
import { Form, Row, Col, Button, Card, Modal, Image, Table } from 'react-bootstrap';
import icNoInsights from './assets/img/ic-empty-box.svg';
import CityInsightsFilter from './CityInsightsFilter';
import Pagination from 'react-bootstrap/Pagination';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import icValidationError from './assets/img/ic-danger.svg';
import icSucess from './assets/img/ic-celebration-circle.svg';

const CityInsights = forwardRef(({ errors }, ref) => {

    useImperativeHandle(ref, () => ({ openModal: () => setShowInsightModal(true) }));
    const { id } = useParams();
    const [insights, setInsights] = useState([]); 
    const [filteredInsights, setFilteredInsights] = useState([]);
    const navigate = useNavigate();
    const [showInsightModal, setShowInsightModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [citiesPerPage, setCitiesPerPage] = useState(10);

    
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentInsights = filteredInsights.slice(indexOfFirst, indexOfLast);

    const [cities, setCities] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);

    const indexOfLastCity = currentPage * citiesPerPage;
    const indexOfFirstCity = indexOfLastCity - citiesPerPage;
    const currentCities = filteredCities.slice(indexOfFirstCity, indexOfLastCity);
   
    const [showDeleteInsightModal, setShowDeleteInsightModal] = useState(false); // Delete modal state
    const [toDelete, setToDelete] = useState({ cityId: null, insightId: null });
    const [saving, setSaving] = useState({}); // insightId -> boolean (disable toggle while saving)

    const [selected, setSelected] = useState(null);
    const handleLocalChange = (e) => { const { name, type, checked, value } = e.target; const v = type === 'checkbox' ? checked : value; setSelected(prev => ({ ...(prev || {}), [name.replace('cityInsight.','')]: v })); };

    const [touched, setTouched] = useState({ 
        cityInsightUrl: false,
        cityInsightProviderUrl: false,
    });

    const [localErrors, setLocalErrors] = useState({
        cityInsightUrl: '' ,
        cityInsightProviderUrl: '',
    });

    // tiny URL validator (same spirit as govUrl)
    const validateCityInsightUrl = (url) => {
        if (!url) return 'URL is required';
        try {
            const u = new URL(url);
            if (!/^https?:$/.test(u.protocol)) return 'URL must start with http:// or https://';
            return '';
        } catch {
            return 'Enter a valid URL';
        }
    };

    const validateCityInsightProviderUrl = (url) => {
        if (!url) return 'URL is required';
        try {
            const u = new URL(url);
            if (!/^https?:$/.test(u.protocol))
            return 'URL must start with http:// or https://';
            return '';
        } catch {
            return 'Enter a valid URL';
        }
    };

    const handleCityInsightProviderURLBlur = () => {
        setTouched((t) => ({ ...t, cityInsightProviderUrl: true }));
        const msg = validateCityInsightProviderUrl(selected?.providerUrl ?? '');
        setLocalErrors((e) => ({ ...e, cityInsightProviderUrl: msg }));
    };

    const handleCityInsightURLBlur = () => {
        setTouched((t) => ({ ...t, cityInsightUrl: true }));
        const msg = validateCityInsightUrl(selected?.url ?? '');
        setLocalErrors((e) => ({ ...e, cityInsightUrl: msg }));
    };

    const insightDefaults = {
        service: '',
        name: '',
        url: '',
        providerUrl: '',
        description: '',
        googleRating: '',
        showStatus: false,
    };
    
    const hasAnyInsight = (ci) => {
        if (!ci) return false;
        const entries = [
            ci.service, ci.name, ci.url, ci.description, ci.providerUrl,
            (ci.googleRating ?? '') !== '' ? String(ci.googleRating) : '',
            ci.showStatus ? '1' : ''
        ];
        return entries.some(v => String(v || '').trim().length > 0);
    };

    const normalizeCity = (city) => {
        const arr = Array.isArray(city.cityInsights)
            ? city.cityInsights
            : (city.cityInsight ? [city.cityInsight] : []); // backward compat

        return arr
        .map((ci) => ({
            ...insightDefaults,
            ...ci,
            googleRating: ci.googleRating === '' || ci.googleRating == null ? '' : String(ci.googleRating),
            showStatus: !!ci.showStatus,
            _id: ci._id || undefined,
        }))
        .filter(hasAnyInsight);
    };

    const fetchCityInsights = useCallback(async () => {
        try {
            const res = await api.get(`/api/cities/${id}`);
            const city = res.data || {};
            const list = normalizeCity(city);
            setInsights(list);
            setFilteredInsights(list);
            setCurrentPage(1);
        } catch (error) {
            console.error('Error fetching city insights:', error);
        }
    }, [id]);

    useEffect(() => {
        fetchCityInsights();
    }, [fetchCityInsights]);

    const handleAddInsight = () => setShowInsightModal(true);

    const [searchQuery, setSearchQuery] = useState('');

    const [sort, setSort] = useState({ by: null, dir: 'asc' });

    // search filter
    useEffect(() => {
        const q = searchQuery.trim().toLowerCase();
        const next = insights.filter((ci) => {
            if (!q) return true;
            return [ci.service, ci.name, ci.url]
            .map(v => String(v || '').toLowerCase())
            .some(s => s.includes(q));
        });
        setFilteredInsights(applySort(next, sort));
        setCurrentPage(1);
    }, [searchQuery, insights]);

    const applySort = (arr, { by, dir }) => {
        if (!by) return arr;
        const mul = dir === 'desc' ? -1 : 1;
        return [...arr].sort((a, b) => {
            const pick = (key, obj) => {
            switch (key) {
                case 'service':                     return (obj.service || '').toLowerCase().trim();
                case 'name':                        return (obj.name || '').toLowerCase().trim();
                case 'url':                         return (obj.url || '').toLowerCase().trim();
                case 'providerUrl':      return (obj.url || '').toLowerCase().trim();
                case 'rating':                      return parseFloat(obj.googleRating || '0') || 0;
                case 'show':                        return obj.showStatus ? 1 : 0;
                default:                            return '';
            }
            };
            const va = pick(by, a);
            const vb = pick(by, b);
            if (va < vb) return -1 * mul;
            if (va > vb) return  1 * mul;
            return 0;
        });
    };

    const toggleSort = (by) => {
        setSort(prev => {
            const dir = prev.by === by ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'asc';
            const next = { by, dir };
            setFilteredInsights(applySort(filteredInsights, next));
            return next;
        });
    };

    const toggleShowStatus = useCallback(
        async (insightId, next) => {
            
            setInsights(prev =>
                prev.map(i => (i._id === insightId ? { ...i, showStatus: next } : i))
            );

            setFilteredInsights(prev =>
                prev.map(i => (i._id === insightId ? { ...i, showStatus: next } : i))
            );

            setSaving(s => ({ ...s, [insightId]: true }));

            try {
                const res = await api.patch(
                    `/api/cities/${id}/insights/${insightId}`,
                    { showStatus: next }
                );
                // rebuild from server truth (expects updated city payload)
                const updated = normalizeCity(res.data || {});
                setInsights(updated);
                setFilteredInsights(updated);
            } catch (err) {
            // revert on error
            setInsights(prev =>
                prev.map(i => (i._id === insightId ? { ...i, showStatus: !next } : i))
            );
            setFilteredInsights(prev =>
                prev.map(i => (i._id === insightId ? { ...i, showStatus: !next } : i))
            );
            console.error('Error toggling showStatus:', err);
            alert('Could not update Show status. Please try again.');
            } finally {
                setSaving(s => {
                    const { [insightId]: _, ...rest } = s;
                    return rest;
                });
            }
        },
        [id]
    );

    const noInsights = filteredInsights.length === 0;

    const totalPages = Math.ceil(filteredInsights.length / rowsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col>
                            <h3 className="mb-0">City Insights</h3>
                        </Col>
                        <Col md={6} className="text-end">
                            <Button onClick={() => { setSelected({ ...insightDefaults }); setShowInsightModal(true); }} variant="outline-secondary" className="btn-sm">
                                Add New City Insight
                            </Button>
                        </Col> 
                    </Row>
                    <hr className="pt-4" />
                     <Row>
                        <Col xs={12}>
                            <CityInsightsFilter
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                insights={insights}
                            />
                        </Col>
                    </Row>

                    {noInsights ? (
                        <Row>
                            <Col md={12}>
                                <Row className="mb-3">
                                    <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                        <Image src={icNoInsights} alt="No City Insights" className="mb-4" />
                                        <h2 className="mb-3">No city insights yet.</h2>
                                        <p className="mb-4">Your city insights will be displayed here.</p>
                                        <Button onClick={() => { setSelected({ ...insightDefaults }); setShowInsightModal(true); }} variant="primary">Add city insights</Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Row>
                                <Col>
                                    <div className="table-responsive list">
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th onClick={() => toggleSort('service')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                        Service
                                                        <span>
                                                            <FaCaretUp className={sort.by === 'service' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sort.by === 'service' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                        </div>
                                                    </th>
                                                    <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                        Provider name
                                                        <span>
                                                            <FaCaretUp className={sort.by === 'name' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sort.by === 'name' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                        </div>
                                                    </th>
                                                    <th onClick={() => toggleSort('url')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                        URL
                                                        <span>
                                                            <FaCaretUp className={sort.by === 'url' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sort.by === 'url' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                        </div>
                                                    </th>
                                                    <th onClick={() => toggleSort('rating')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                        Google rating
                                                        <span>
                                                            <FaCaretUp className={sort.by === 'rating' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sort.by === 'rating' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                        </div>
                                                    </th>
                                                    <th onClick={() => toggleSort('show')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                        Show
                                                        <span>
                                                            <FaCaretUp className={sort.by === 'show' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sort.by === 'show' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                        </div>
                                                    </th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentInsights.map((ins) => {
                                                    const href = (() => {
                                                    if (!ins.url) return null;
                                                    try {
                                                        const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(ins.url) ? ins.url : `https://${ins.url}`;
                                                        const u = new URL(withScheme);
                                                        return ['http:', 'https:'].includes(u.protocol) ? u.toString() : null;
                                                    } catch { return null; }
                                                    })();

                                                    return (
                                                        <tr key={ins._id || `${ins.name}.${ins.service}`}>
                                                            <td>{ins.service || '—'}</td>
                                                            <td>{ins.name || '—'}</td>
                                                            <td>{href ? <a href={href} target="_blank" rel="noopener noreferrer">{ins.url}</a> : '—'}</td>
                                                            <td>{(ins.googleRating ?? '') === '' ? '—' : ins.googleRating}</td>
                                                            <td>
                                                                <div className="form-check form-switch">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={!!ins.showStatus}
                                                                        disabled={!!saving[ins._id]}
                                                                        onChange={() => toggleShowStatus(ins._id, !ins.showStatus)}
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td className="text-end">
                                                                <Button
                                                                    className="btn-sm"
                                                                    variant="outline-secondary"
                                                                    onClick={() => {
                                                                        setSelected(ins);
                                                                        setShowInsightModal(true);
                                                                    }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    className="btn-sm ms-2"
                                                                    onClick={() => {
                                                                        setToDelete({ insightId: ins._id });
                                                                        setShowDeleteInsightModal(true);
                                                                    }}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>

                                        </Table>
                                    </div>
                                </Col>
                            </Row>
                            {/* Pagination Row with Total Count, Items Per Page Selector & "Go to Page" Input */}
                            <Row className="pagination-wrapper">
                                <Col className="d-flex align-items-center">
                                    <span>Total&nbsp;{filteredInsights.length}&nbsp;Insights</span>
                                    <Pagination>
                                        <Pagination.Prev 
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                                            disabled={currentPage === 1}
                                        />
                                        {[...Array(totalPages).keys()].map(number => (
                                            <Pagination.Item 
                                                key={number + 1} 
                                                active={number + 1 === currentPage} 
                                                onClick={() => handlePageChange(number + 1)}
                                            >
                                                {number + 1}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next 
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </Col>
                                <Col className="pagination-controls">
                                    <div className="pagination-controls-per-page">
                                        <Form.Select
                                            value={rowsPerPage}
                                            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                            style={{ width: 'auto', marginRight: '10px' }}
                                        >
                                            <option value="10">10 / page</option>
                                            <option value="20">20 / page</option>
                                            <option value="40">40 / page</option>
                                        </Form.Select>
                                    </div>
                                    <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                        Go to
                                    </label>
                                    <Form.Control
                                        type="number"
                                        className="pagination-controls-goto-input"
                                        value={currentPage}
                                        onChange={(e) => {
                                            const newPage = Number(e.target.value);
                                            if (newPage >= 1 && newPage <= totalPages) {
                                                setCurrentPage(newPage); // ✅ Only valid pages
                                            }
                                        }}
                                        onBlur={() => {
                                            if (currentPage < 1) setCurrentPage(1);
                                            if (currentPage > totalPages) setCurrentPage(totalPages);
                                        }}
                                        style={{ textAlign: 'center' }}
                                    />
                                    
                                </Col>
                            </Row>
                            
                        </>
                    )}
                </Card.Body>
            </Card>

            <Modal className="with-form" show={showInsightModal} onHide={() => { setShowInsightModal(false); setSelected({ ...insightDefaults }); }} centered>
                <Form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        setSelected({ ...insightDefaults });
                        try {
                            const payload = {
                                service: selected?.service ?? '',
                                name: selected?.name ?? '',
                                url: selected?.url ?? '',
                                providerUrl: selected?.providerUrl ?? '',
                                description: selected?.description ?? '',
                                googleRating: selected?.googleRating ?? '',
                                showStatus: !!selected?.showStatus,
                            };

                            if (selected?._id) {
                                // Edit existing
                                await api.patch(
                                    `/api/cities/${id}/insights/${selected._id}`,
                                    payload
                                );
                            } else {
                                // Add new
                                await api.post(
                                    `/api/cities/${id}/insights`,
                                    payload
                                );
                            }

                            await fetchCityInsights(); // refresh list
                            setShowInsightModal(false);
                            setShowSuccessModal(true);
                            } catch (err) {
                                console.error('Save insight failed:', err);
                                alert('Could not save the insight. Please try again.');
                            }
                        }}
                    >
                    <Modal.Header closeButton>
                        <Modal.Title>{selected?._id ? 'Edit Insight' : 'Add New Insight'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Service</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="cityInsight.service"
                                        placeholder="Enter service name"
                                        value={selected?.service ?? ''}
                                        onChange={handleLocalChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>  
                                <Form.Group className="mb-3">
                                    <Form.Label>Provider</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="cityInsight.name"
                                        value={selected?.name ?? ''}
                                        onChange={handleLocalChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group className="validated mb-3" controlId="cityInsightUrl">
                                    <Form.Label>URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="cityInsight.url"
                                        value={selected?.url ?? ''}
                                        onChange={handleLocalChange}
                                        onBlur={handleCityInsightURLBlur} // ✅
                                        placeholder="example.com"
                                        inputMode="url"
                                        isInvalid={touched.cityInsightUrl && !!(localErrors?.cityInsightUrl || errors?.cityInsightUrl)}
                                        aria-describedby="cityInsightUrlFeedback"
                                    />
                                    {!!(localErrors?.cityInsightUrl || errors?.cityInsightUrl) && (
                                        <div className="custom-error-icon-wrapper">
                                            <img
                                                src={icValidationError}
                                                alt={localErrors?.cityInsightUrl || errors?.cityInsightUrl}
                                                className="custom-error-icon"
                                            />
                                            <div className="custom-tooltip">
                                                {localErrors?.cityInsightUrl || errors?.cityInsightUrl}
                                            </div>
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="cityInsight.description"
                                        value={selected?.description ?? ''}
                                        onChange={handleLocalChange}
                                        style={{ height: '77px' }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Google rating</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="cityInsight.googleRating"
                                        min="0"
                                        max="5"
                                        step="0.1" // 0.1 ⇒ 4.2, 3.7 etc.
                                        value={selected?.googleRating ?? ''}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            const val = raw === '' ? '' : Number(raw); // keep empty string when cleared
                                            handleLocalChange({
                                                target: { name: e.target.name, value: val },
                                            });
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="validated" controlId="cityInsightProviderUrl">
                                    <Form.Label>
                                        Rating Provider URL <span>*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="cityInsight.providerUrl"
                                        value={selected?.providerUrl ?? ''}
                                        onChange={handleLocalChange}
                                        onBlur={handleCityInsightProviderURLBlur} // ✅ custom blur validator
                                        placeholder="example.com"
                                        inputMode="url"
                                        isInvalid={
                                        touched.cityInsightProviderUrl &&
                                        !!(localErrors?.cityInsightProviderUrl || errors?.cityInsightProviderUrl)
                                        }
                                        aria-describedby="cityInsightProviderUrlFeedback"
                                        required
                                    />
                                    {!!(localErrors?.cityInsightProviderUrl || errors?.cityInsightProviderUrl) && (
                                        <div className="custom-error-icon-wrapper">
                                        <img
                                            src={icValidationError}
                                            alt={
                                            localErrors?.cityInsightProviderUrl || errors?.cityInsightProviderUrl
                                            }
                                            className="custom-error-icon"
                                        />
                                        <div className="custom-tooltip">
                                            {localErrors?.cityInsightProviderUrl || errors?.cityInsightProviderUrl}
                                        </div>
                                        </div>
                                    )}
                                </Form.Group>

                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <div className="visible-toggle d-flex align-items-center justify-content-between">
                                    <p className="visible-toggle__label">Visible</p>

                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Check
                                            type="switch"
                                            id="cityInsight-showStatus"
                                            className="visible-toggle__switch"
                                            name="cityInsight.showStatus"
                                            checked={!!selected?.showStatus}
                                            onChange={(e) =>
                                                handleLocalChange({
                                                    target: { name: e.target.name, value: e.target.checked },
                                                })
                                            }
                                        />
                                        <p className="visible-toggle__state">
                                            {selected?.showStatus ? 'Yes' : 'No'}
                                        </p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => { setShowInsightModal(false); setSelected({ ...insightDefaults }); }}>Cancel</Button>
                        <Button variant="primary" className="ms-3" type="submit">Save</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteInsightModal} onHide={() => setShowDeleteInsightModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this city? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteInsightModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="outline-danger"
                        onClick={async () => {
                            if (!toDelete.insightId) return;
                            try {
                                const res = await api.delete(`/api/cities/${id}/insights/${toDelete.insightId}`);

                                // Rebuild the local list from the updated city payload
                                const updated = normalizeCity(res.data || {});
                                setInsights(updated);

                                setFilteredInsights(
                                    applySort(
                                    updated.filter(ci => {
                                        const q = searchQuery.trim().toLowerCase();
                                        if (!q) return true;
                                        return [ci.service, ci.name, ci.url]
                                        .map(v => String(v || '').toLowerCase())
                                        .some(s => s.includes(q));
                                    }),
                                    sort
                                    )
                                );
                            } catch (err) {
                                console.error('Error deleting insight:', err);
                                alert('An error occurred while deleting the city insight.');
                            } finally {
                                setShowDeleteInsightModal(false);
                                setToDelete({ insightId: null });
                            }
                        }}
                    >
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">City insight successfully created</h2>
                    <p className="text-center">I would like to confirm that the city insight has been successfully created in the system.</p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    <Button variant="primary" onClick={() => navigate('/onboarding-city-list')}>
                        Go back to cities
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
});

export default CityInsights;
